import { WalletOutlined, CopyOutlined, PlusOutlined, EditOutlined, DeleteOutlined, BoldOutlined, FontColorsOutlined, LinkOutlined, VideoCameraOutlined, CodeOutlined } from "@ant-design/icons";
import {
  AutoComplete,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  TreeSelect,
  Select,
  Tabs,
  Upload,
  Table,
  Space,
  Popconfirm,
  Modal,
  Tag,
  Tooltip as AntTooltip,
} from "antd";
import {
  useContext,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useRef,
} from "react";
import { API } from "src/shared";
import { NomenclatureContext } from "src/shared/lib/hooks/context/getNomenclatureContext";
import { PricesContext } from "src/shared";

import debounce from "lodash.debounce";
import { apiRequests } from "src/shared/api/api";
import { lazy, Suspense } from "react";

const AddPrice = lazy(() =>
  import("src/features/Modal/ui/AddPrice").then((module) => ({
    default: module.default,
  }))
);

const EditPrice = lazy(() =>
  import("src/features/Modal/ui/EditPrice").then((module) => ({
    default: module.default,
  }))
);

// ─── HTML Rich-Text Editor ─────────────────────────────────────────────────
const HtmlEditor = ({ value = "", onChange }) => {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);

  // Sync incoming value into the editor on mount / external change
  useEffect(() => {
    if (editorRef.current && !showSource) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const exec = (command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    notifyChange();
  };

  const applyColor = (color) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, color);
    notifyChange();
  };

  const applyBgColor = (color) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("hiliteColor", false, color);
    notifyChange();
  };

  const notifyChange = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const toggleSource = () => {
    if (!showSource) {
      // switch to source view
      setSourceValue(editorRef.current?.innerHTML || "");
      setShowSource(true);
    } else {
      // apply source back to editor
      if (editorRef.current) {
        editorRef.current.innerHTML = sourceValue;
      }
      onChange && onChange(sourceValue);
      setShowSource(false);
    }
  };

  const toolbarBtnStyle = {
    padding: "4px 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#fafafa",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: "20px",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 6, overflow: "hidden" }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          padding: "6px 8px",
          background: "#f5f5f5",
          borderBottom: "1px solid #d9d9d9",
        }}
      >
        <AntTooltip title="Жирный">
          <button type="button" style={toolbarBtnStyle} onMouseDown={(e) => { e.preventDefault(); exec("bold"); }}>
            <BoldOutlined />
          </button>
        </AntTooltip>
        <AntTooltip title="Курсив">
          <button type="button" style={toolbarBtnStyle} onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}>
            <i style={{ fontStyle: "italic", fontFamily: "serif" }}>I</i>
          </button>
        </AntTooltip>
        <AntTooltip title="Подчёркнутый">
          <button type="button" style={toolbarBtnStyle} onMouseDown={(e) => { e.preventDefault(); exec("underline"); }}>
            <u>U</u>
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip title="Красный текст">
          <button
            type="button"
            style={{ ...toolbarBtnStyle, color: "#f5222d" }}
            onMouseDown={(e) => { e.preventDefault(); applyColor("#f5222d"); }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Красный</span>
          </button>
        </AntTooltip>
        <AntTooltip title="Белый текст (на тёмном фоне)">
          <button
            type="button"
            style={{ ...toolbarBtnStyle, background: "#333", color: "#fff", borderColor: "#555" }}
            onMouseDown={(e) => { e.preventDefault(); applyColor("#ffffff"); }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Белый</span>
          </button>
        </AntTooltip>
        <AntTooltip title="Чёрный текст">
          <button
            type="button"
            style={{ ...toolbarBtnStyle }}
            onMouseDown={(e) => { e.preventDefault(); applyColor("#000000"); }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Чёрный</span>
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip title="Выделение жёлтым">
          <button
            type="button"
            style={{ ...toolbarBtnStyle, background: "#ffe58f" }}
            onMouseDown={(e) => { e.preventDefault(); applyBgColor("#ffe58f"); }}
          >
            ✏️ Фон
          </button>
        </AntTooltip>
        <AntTooltip title="Убрать форматирование">
          <button
            type="button"
            style={toolbarBtnStyle}
            onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }}
          >
            ✕ Сброс
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip title={showSource ? "Вернуться к редактору" : "Редактировать HTML"}>
          <button
            type="button"
            style={{ ...toolbarBtnStyle, background: showSource ? "#1677ff" : "#fff", color: showSource ? "#fff" : undefined, borderColor: showSource ? "#1677ff" : undefined }}
            onMouseDown={(e) => { e.preventDefault(); toggleSource(); }}
          >
            <CodeOutlined /> HTML
          </button>
        </AntTooltip>
      </div>

      {/* Source editor */}
      {showSource ? (
        <Input.TextArea
          value={sourceValue}
          rows={6}
          onChange={(e) => setSourceValue(e.target.value)}
          style={{ borderRadius: 0, fontFamily: "monospace", fontSize: 12, border: "none", resize: "vertical" }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={notifyChange}
          onBlur={notifyChange}
          style={{
            minHeight: 120,
            padding: "8px 12px",
            outline: "none",
            lineHeight: 1.6,
            fontSize: 14,
            background: "#fff",
          }}
        />
      )}
    </div>
  );
};

// ─── Video Add/Edit Modal ──────────────────────────────────────────────────
const VideoFormModal = ({ open, onClose, onSave, initialValues }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(
        initialValues || { url: "", description: "", tags: [] }
      );
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
      form.resetFields();
    } catch (_) {}
  };

  return (
    <Modal
      open={open}
      title={initialValues ? "Редактировать видео" : "Добавить видео"}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleOk}
      okText="Сохранить"
      cancelText="Отмена"
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item
          label="Ссылка на видео"
          name="url"
          rules={[{ required: true, message: "Укажите ссылку на видео" }]}
        >
          <Input prefix={<LinkOutlined />} placeholder="https://youtube.com/watch?v=..." />
        </Form.Item>
        <Form.Item label="Описание видео" name="description">
          <Input.TextArea rows={3} placeholder="Краткое описание видео" />
        </Form.Item>
        <Form.Item label="Теги" name="tags">
          <Select
            mode="tags"
            style={{ width: "100%" }}
            tokenSeparators={[","]}
            placeholder="Добавьте теги через запятую"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const formItemLayout = { labelCol: { span: 5 }, wrapperCol: { span: 17 } };

const Nomenclature = forwardRef(
  (
    {
      formContext,
      record,
      handleSaveImage,
      handleDeleteImage,
      setChangeData,
      withoutImage = false,
      hideTabs = false,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage();
    const {
      manufacturersData = [],
      categoriesData = [],
      unitsData = [],
      token,
    } = useContext(NomenclatureContext);
    const [manufacturers, setManufacturers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pictures, setPictures] = useState([]);
    const [units, setUnits] = useState([]);
    const [nomenclature, setNomenclature] = useState([]);
    const [optionsName, setOptionsName] = useState([]);
    const [optionsLocation, setOptionsLocations] = useState([]);
    // legacy (плоский список) больше не используется
    // const [globalCategories, setGlobalCategories] = useState([]);
    const [cashbackType, setCashbackType] = useState(
      record && record.cashback_type ? record.cashback_type : "lcard_cashback"
    );
    const [activeTab, setActiveTab] = useState("main");
    const [globalCategoriesTree, setGlobalCategoriesTree] = useState([]);
    const [prices, setPrices] = useState([]);
    const [priceTypes, setPriceTypes] = useState([]);
    const [loadingPrices, setLoadingPrices] = useState(false);
    const [isAddPriceModalOpen, setIsAddPriceModalOpen] = useState(false);
    const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);
    const [editingPriceRecord, setEditingPriceRecord] = useState(null);

    // ── Videos state ─────────────────────────────────────────────────────────
    const [videos, setVideos] = useState([]);
    const [isAddVideoOpen, setIsAddVideoOpen] = useState(false);
    const [isEditVideoOpen, setIsEditVideoOpen] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);

    useImperativeHandle(ref, () => ({
      setActiveTab: (tabKey) => {
        setActiveTab(tabKey);
      },
    }));

    const types = [
      { value: "product", label: "Товар" },
      { value: "service", label: "Услуга" },
      { value: "offer", label: "Предложение" },
      { value: "resourse", label: "Ресурс" },
      { value: "rental", label: "Аренда" },
      { value: "property", label: "Недвижимость" },
      { value: "work", label: "Работа" },
    ];

    const { TextArea } = Input;

    const getNomenclature = API.crud.get(token, "/nomenclature");

    const validateMessages = {
      /* eslint-disable no-template-curly-in-string */
      required: "Поле обязательно для ввода!",
    };

    // Загрузка дерева глобальных категорий для TreeSelect
    useEffect(() => {
      const loadGlobalCategoriesTree = async () => {
        try {
          const res = await fetch(
            `https://${process.env.REACT_APP_APP_URL}/api/v1/mp/categories/tree-select/`,
          );
          const data = await res.json();
          if (data?.result) {
            setGlobalCategoriesTree(data.result);
          }
        } catch (err) {
          console.error("Ошибка загрузки дерева глобальных категорий:", err);
        }
      };
      loadGlobalCategoriesTree();
    }, []);

    // Автозаполнение адреса
    const onChangeLocation = async (value) => {
      if (!value) {
        setOptionsLocations([]);
        return;
      }
      try {
        const res = await fetch(
          `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation?query=${encodeURIComponent(
            value,
          )}&limit=5`,
        );
        const data = await res.json();
        if (data?.suggestions?.length) {
          setOptionsLocations(
            data.suggestions.map((suggest) => ({
              value: suggest,
              label: suggest,
            })),
          );
        } else {
          setOptionsLocations([
            {
              value: value,
              label: value,
            },
          ]);
        }
      } catch (err) {
        console.error("Ошибка загрузки адресов:", err);
      }
    };

    const debounceOnChangeLocation = debounce(onChangeLocation, 500);

    // Получение координат для выбранного адреса
    const handleAddressSelect = async (address) => {
      formContext.setFieldsValue({ address: address });

      // Получаем координаты для выбранного адреса
      try {
        const res = await fetch(
          `https://${process.env.REACT_APP_APP_URL}/api/v1/autosuggestions/geolocation/validate?address=${encodeURIComponent(
            address,
          )}`,
        );
        const data = await res.json();

        if (data?.latitude !== undefined && data?.longitude !== undefined) {
          formContext.setFieldsValue({
            latitude: data.latitude,
            longitude: data.longitude,
          });
        }
      } catch (err) {
        console.error("Ошибка получения координат:", err);
      }
    };

    const onChangeName = (value) => {
      formContext.setFieldsValue({ name: value }); // Update form value directly
      setOptionsName(
        nomenclature.filter((n) =>
          n.value.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    };

    const debounceOnChangeName = debounce(onChangeName, 500);

    useEffect(() => {
      if (unitsData.length !== 0) {
        const unitsSelect = [];
        for (let item of unitsData) {
          unitsSelect.push({ value: item.id, label: item.name });
        }
        setUnits(unitsSelect);
      }
      if (manufacturersData.length !== 0) {
        const manufacturersSelect = [];
        for (let item of manufacturersData) {
          manufacturersSelect.push({ value: item.id, label: item.name });
        }
        setManufacturers(manufacturersSelect);
      }
      if (categoriesData.length !== 0) {
        const categoriesSelect = [];
        for (let item of categoriesData) {
          categoriesSelect.push({ value: item.id, label: item.name });
        }
        setCategories(categoriesSelect);
      }
      if (record?.pictures !== undefined && record?.pictures.length !== 0) {
        const picturesDefault = [];
        for (let item of record.pictures) {
          picturesDefault.push({
            id: item.id,
            name: item.url,
            status: "done",
            url: `https://${process.env.REACT_APP_APP_URL}/api/v1/${item.url}/`,
          });
        }
        setPictures(picturesDefault);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manufacturersData, categoriesData, unitsData]);

    useEffect(() => {
      const initial = async () => {
        await getNomenclature().then((res) =>
          setNomenclature(res.result.map((el) => ({ value: el.name }))),
        );
      };

      if (!nomenclature.length) {
        initial();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (record?.id) {
        apiRequests.pictures
          .getItems({
            entity: "nomenclature",
            entity_id: record.id,
          })
          .then((response) => {
            setPictures(
              response.data.result.map((item) => ({
                id: item.id,
                name: item.url,
                status: "done",
                url: `https://${process.env.REACT_APP_APP_URL}/api/v1/${item.url}/`,
              })),
            );
          });
      }
    }, [record?.id]);

    // Загрузка типов цен
    useEffect(() => {
      const loadPriceTypes = async () => {
        try {
          const response = await apiRequests.priceTypes.getItems({ token });
          const types = response?.data?.result || response?.data || [];
          setPriceTypes(types);
        } catch (error) {
          console.error("Ошибка загрузки типов цен:", error);
        }
      };
      if (token) {
        loadPriceTypes();
      }
    }, [token]);

    // Загрузка цен для номенклатуры
    const loadPrices = useCallback(async () => {
      if (!record?.id || !token) {
        setPrices([]);
        return;
      }
      setLoadingPrices(true);
      try {
        const response = await apiRequests.prices.getItems({
          token,
          limit: 10000,
        });
        const allPrices = Array.isArray(response?.data?.result)
          ? response.data.result
          : [];
        // Фильтруем цены для текущей номенклатуры (исключаем undefined/null)
        const nomenclaturePrices = allPrices
          .filter(Boolean)
          .filter(
            (price) => (price.nomenclature_id || price.nomenclature) === record.id
          );
        setPrices(nomenclaturePrices);
      } catch (error) {
        console.error("Ошибка загрузки цен:", error);
        messageApi.error("Не удалось загрузить цены");
      } finally {
        setLoadingPrices(false);
      }
    }, [record?.id, token, messageApi]);

    // Синхронизация marketplace_price в отдельную цену типа "chatting"
    const syncMarketplacePriceToPrices = async () => {
      const marketplacePrice = formContext.getFieldValue("marketplace_price");
      if (
        marketplacePrice === undefined ||
        marketplacePrice === null ||
        marketplacePrice === ""
      ) {
        return;
      }

      if (!record?.id || !token) {
        return;
      }

      // Ищем тип цены "chatting"
      const chattingType = priceTypes.find(
        (pt) => pt?.name === "chatting" || pt?.code === "chatting",
      );
      const chattingTypeId = chattingType?.id;

      if (!chattingTypeId) {
        // eslint-disable-next-line no-console
        console.warn(
          'Не найден тип цены "chatting" в списке типов цен. Маркетплейс-цена не будет синхронизирована.',
        );
        return;
      }

      const numericPrice = Number(marketplacePrice);
      if (Number.isNaN(numericPrice)) {
        messageApi.error("Цена для маркетплейса должна быть числом");
        return;
      }

      // Ищем существующую цену с типом chatting для этой номенклатуры
      const existingChattingPrice = prices.find((price) => {
        const priceNomenclature = price.nomenclature || price.nomenclature_id;
        const priceTypeRaw = price.price_type;
        const isChattingByName =
          typeof priceTypeRaw === "string" && priceTypeRaw === "chatting";
        const isChattingById =
          typeof priceTypeRaw === "number" && priceTypeRaw === chattingTypeId;
        return priceNomenclature === record.id && (isChattingByName || isChattingById);
      });

      try {
        if (existingChattingPrice?.id) {
          // Обновляем существующую цену
          await apiRequests.prices.patch({
            id: existingChattingPrice.id,
            price: numericPrice,
            price_type: chattingTypeId,
            nomenclature: record.id,
          });
        } else {
          // Создаем новую цену
          await apiRequests.prices.post([
            {
              nomenclature: record.id,
              price: numericPrice,
              price_type: chattingTypeId,
            },
          ]);
        }

        // Перезагружаем цены, чтобы таблица и состояние были актуальны
        await loadPrices();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Ошибка синхронизации цены маркетплейса:", error);
        const errMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Не удалось сохранить цену для маркетплейса";
        messageApi.error(errMessage);
      }
    };

    // Загружаем цены при открытии вкладки "Цены" или изменении record.id
    useEffect(() => {
      if (activeTab === "prices" && record?.id) {
        loadPrices();
      }
    }, [activeTab, record?.id, loadPrices]);


    // ── Videos: load from API ────────────────────────────────────────────────
    const [loadingVideos, setLoadingVideos] = useState(false);

    const loadVideos = useCallback(async () => {
      if (!record?.id || !token) {
        setVideos([]);
        return;
      }
      setLoadingVideos(true);
      try {
        const response = await apiRequests.nomenclatureVideos.getItems(record.id, { token });
        const items = response?.data?.result || response?.data || [];
        setVideos(items);
      } catch (error) {
        console.error("Ошибка загрузки видео:", error);
        messageApi.error("Не удалось загрузить видео");
      } finally {
        setLoadingVideos(false);
      }
    }, [record?.id, token, messageApi]);

    // Загружаем видео при открытии вкладки "Видео" или смене записи
    useEffect(() => {
      if (activeTab === "videos" && record?.id) {
        loadVideos();
      }
    }, [activeTab, record?.id, loadVideos]);

    const filterOption = (input, option) =>
      (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

    const onValuesChange = () => {
      setChangeData(true);
    };

    // Обновляем значения формы при изменении record
    useEffect(() => {
      if (record) {
        // Устанавливаем все значения из record, включая qr_hash
        formContext.setFieldsValue(record);
      } else {
        // При создании новой номенклатуры устанавливаем значение по умолчанию для типа кешбека
        formContext.setFieldsValue({ cashback_type: "lcard_cashback" });
      }
    }, [record, formContext]);

    // При открытии редактора подтягиваем цену для маркетплейса из alt_prices по ID номенклатуры
    useEffect(() => {
      if (!record?.id || !token) return;

      let isCancelled = false;

      const fetchMarketplacePrice = async () => {
        try {
          const response = await apiRequests.prices.getAltItem(record.id, {
            token,
          });
          const data = response?.data || {};
          const price = data.price;

          if (!isCancelled && price !== undefined && price !== null) {
            formContext.setFieldsValue({
              marketplace_price: Number(price),
            });
          }
        } catch (error) {
          // Тихо логируем, чтобы не мешать загрузке формы
          // eslint-disable-next-line no-console
          console.warn("Не удалось получить цену маркетплейса через alt_prices:", error);
        }
      };

      fetchMarketplacePrice();

      return () => {
        isCancelled = true;
      };
    }, [record?.id, token, formContext]);

    return (
      <>
        {contextHolder}
        <Form
          {...formItemLayout}
          form={formContext}
          initialValues={record || { cashback_type: "lcard_cashback" }}
          onValuesChange={onValuesChange}
          layout={"horizontal"}
          style={{ maxWidth: "100%", marginTop: 16 }}
          validateMessages={validateMessages}
          scrollToFirstError
        >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={hideTabs ? { display: "none" } : undefined}
          items={[
            {
              key: "main",
              label: "Основное",
              children: (
                <>
                  <Form.Item
                    rules={[{ required: true }]}
                    label={"Имя"}
                    name={"name"}
                  >
                    <AutoComplete
                      options={optionsName}
                      onSearch={debounceOnChangeName}
                      onChange={(value) => {
                        formContext.setFieldsValue({ name: value });
                      }}
                      filterOption={false}
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item label={"Тип"} name={"type"}>
                    <Select placeholder="Выберите тип">
                      {types.map((t) => (
                        <Select.Option key={t.value} value={t.value}>
                          {t.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Теги" name="tags">
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      tokenSeparators={[","]}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Краткое описание"
                    name={"description_short"}
                  >
                    <TextArea rows={4} />
                  </Form.Item>
                  <Form.Item
                    label="Длинное описание"
                    name={"description_long"}
                    getValueFromEvent={(val) => val}
                  >
                    <HtmlEditor />
                  </Form.Item>
                  <Form.Item label={"Код"} name={"code"}>
                    <Input />
                  </Form.Item>
                  <Form.Item label={"Единица измерения"} name={"unit"}>
                    <Select filterOption={filterOption} showSearch options={units} />
                  </Form.Item>
                  <Form.Item label="Категория" name={"category"}>
                    <Select
                      filterOption={filterOption}
                      showSearch
                      options={categories}
                    />
                  </Form.Item>
                  <Form.Item label={"Производитель"} name={"manufacturer"}>
                    <Select
                      filterOption={filterOption}
                      showSearch
                      options={manufacturers}
                    />
                  </Form.Item>
                  <Form.Item label={"Тип кешбека"} name={"cashback_type"}>
                    <Select value={cashbackType} onChange={setCashbackType}>
                      <Select.Option value={"percent"}>Проценты</Select.Option>
                      <Select.Option value={"const"}>Постоянный</Select.Option>
                      <Select.Option value={"lcard_cashback"}>
                        По карте лояльности
                      </Select.Option>
                      <Select.Option value={"no_cashback"}>Отсутствует</Select.Option>
                    </Select>
                  </Form.Item>
                  {cashbackType === "percent" || cashbackType === "const" ? (
                    <Form.Item label={"Кешбек"} name={"cashback_value"}>
                      <Input
                        placeholder={
                          cashbackType === "percent"
                            ? "В процентах"
                            : cashbackType === "const"
                              ? "В баллах"
                              : ""
                        }
                        addonAfter={
                          cashbackType === "percent" ? "%" : <WalletOutlined />
                        }
                      />
                    </Form.Item>
                  ) : null}
                  {!withoutImage ? (
                    <Form.Item label={"Изображение"}>
                      <Upload
                        action={`https://${process.env.REACT_APP_APP_URL}/api/v1/pictures/?token=${token}&entity=nomenclature&entity_id=${record.id}`}
                        fileList={pictures}
                        listType="picture-card"
                        onChange={({
                          fileList: newFileList,
                          file: newFile,
                        }) => {
                          if (newFile.status === "done") {
                            handleSaveImage(newFile.response);
                            setPictures(newFileList);
                            return message.success(
                              "Изображение было добавлено",
                            );
                          }
                          setPictures(newFileList);
                        }}
                        onRemove={(picture) => {
                          if (picture.response !== undefined) {
                            return handleDeleteImage(picture.response.id);
                          }
                          handleDeleteImage(picture.id);
                        }}
                      >
                        <Button>Загрузить</Button>
                      </Upload>
                      {/* <div style={{padding: '10px 0', display: 'flex', gap: 10}}>
                  {
                    pics.map(pic => <PreviewImage items={[pic]} width={60} height={60} key={pic.id} />)
                  }
                </div> */}
                    </Form.Item>
                  ) : null}
                </>
              ),
            },
            {
              key: "seo",
              label: "SEO",
              children: (
              <>
                  <Form.Item label={"SEO Title"} name={"seo_title"}>
                    <Input placeholder="Заголовок для поисковиков" />
                  </Form.Item>

                  <Form.Item label={"SEO Description"} name={"seo_description"}>
                    <TextArea
                      rows={3}
                      placeholder="Краткое описание для поисковиков"
                    />
                  </Form.Item>

                  <Form.Item label={"SEO Keywords"} name={"seo_keywords"}>
                    <Select
                      mode="tags"
                      style={{ width: "100%" }}
                      tokenSeparators={[","]}
                      placeholder="Ключевые слова"
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "quick",
              label: "Быстро",
              children: (
                <>
                  <Form.Item
                    label={"Глобальная категория"}
                    name={"global_category_id"}
                  >
                    <TreeSelect
                      style={{ width: "100%" }}
                      treeData={globalCategoriesTree}
                      placeholder="Выберите глобальную категорию"
                      treeDefaultExpandAll={false}
                      showSearch
                      allowClear
                      treeLine
                      filterTreeNode={(input, node) =>
                        (node?.title || "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span style={{ whiteSpace: "normal" }}>
                        Цена для маркетплейса
                      </span>
                    }
                    name={"marketplace_price"}
                    tooltip="Цена товара для отображения на маркетплейсе"
                    normalize={(value) => (value === "" ? undefined : value)}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      addonAfter="₽"
                      onBlur={syncMarketplacePriceToPrices}
                    />
                  </Form.Item>

                  <Form.Item
                    label={"Комиссия маркета"}
                    name={"chatting_percent"}
                    tooltip="Процент комиссии маркетплейса (4-100), округляется кратно 4%"
                    extra="Минимум 4%. Значение приводится к ближайшему кратному 4% (4–100)"
                    normalize={(value) => (value === "" ? undefined : value)}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={4}
                      max={100}
                      step={4}
                      precision={0}
                    placeholder="4–100"
                      addonAfter="%"
                      onBlur={() => {
                        const current = formContext.getFieldValue("chatting_percent");
                        if (current === null || current === undefined) return;
                        const rounded = Math.round(Number(current) / 4) * 4;
                        const clamped = Math.min(100, Math.max(4, rounded));
                        if (clamped !== current) {
                          formContext.setFieldsValue({ chatting_percent: clamped });
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={"QR-hash"}
                    name={"qr_hash"}
                    tooltip="QR-hash генерируется автоматически при сохранении номенклатуры"
                  >
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.qr_hash !== curr.qr_hash}>
                      {() => {
                        const fullHash = formContext.getFieldValue("qr_hash");
                        const trimmedHash =
                          typeof fullHash === "string" && fullHash.includes("nm_")
                            ? fullHash.slice(fullHash.indexOf("nm_"))
                            : fullHash;

                        const handleCopy = async () => {
                          const textToCopy = trimmedHash || fullHash;
                          if (!textToCopy) {
                            setTimeout(() => {
                              messageApi.warning("Нет QR-hash для копирования");
                            }, 0);
                            return;
                          }

                          // Пытаемся через Clipboard API, при неудаче — fallback через textarea
                          const fallbackCopy = () => {
                            try {
                              const textarea = document.createElement("textarea");
                              textarea.value = textToCopy;
                              textarea.setAttribute("readonly", "");
                              textarea.style.position = "absolute";
                              textarea.style.left = "-9999px";
                              document.body.appendChild(textarea);
                              textarea.select();
                              const ok = document.execCommand("copy");
                              document.body.removeChild(textarea);
                              return ok;
                            } catch (err) {
                              return false;
                            }
                          };

                          try {
                            await navigator.clipboard.writeText(textToCopy);
                            setTimeout(() => {
                              messageApi.success("QR-hash скопирован");
                            }, 0);
                          } catch (err) {
                            const ok = fallbackCopy();
                            if (ok) {
                              setTimeout(() => {
                                messageApi.success("QR-hash скопирован");
                              }, 0);
                            } else {
                              setTimeout(() => {
                                messageApi.error("Не удалось скопировать QR-hash");
                              }, 0);
                              console.error("Clipboard copy failed:", err);
                            }
                          }
                        };

                        return (
                          <Input
                            disabled
                            value={trimmedHash}
                            placeholder="Автоматически генерируется при сохранении"
                            addonAfter={
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                                disabled={!fullHash}
                                aria-label="Скопировать QR-hash"
                                style={{
                                  padding: "4px 8px",
                                  height: "auto",
                                  border: "none",
                                  boxShadow: "none",
                                }}
                              />
                            }
                          />
                        );
                      }}
                    </Form.Item>
                  </Form.Item>

                  <Form.Item
                    label={"Адрес"}
                    name={"address"}
                    tooltip="При выборе адреса широта и долгота заполняются автоматически"
                  >
                    <AutoComplete
                      options={optionsLocation}
                      onSearch={debounceOnChangeLocation}
                      onSelect={handleAddressSelect}
                      filterOption={false}
                      allowClear
                      placeholder="Введите адрес"
                    />
                  </Form.Item>

                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    <Form.Item
                      label={"Широта"}
                      name={"latitude"}
                      style={{ flex: 1, marginBottom: 0 }}
                      normalize={(value) =>
                        value === "" || value === null ? undefined : value
                      }
                      tooltip="Автоматически заполняется при выборе адреса"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        step={0.000001}
                        placeholder="55.751244"
                        disabled
                      />
                    </Form.Item>

                    <Form.Item
                      label={"Долгота"}
                      name={"longitude"}
                      style={{ flex: 1, marginBottom: 0 }}
                      normalize={(value) =>
                        value === "" || value === null ? undefined : value
                      }
                      tooltip="Автоматически заполняется при выборе адреса"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        step={0.000001}
                        placeholder="37.618423"
                        disabled
                      />
                    </Form.Item>
                  </div>
                </>
              ),
            },
            {
              key: "prices",
              label: "Цены",
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        if (!record?.id) {
                          messageApi.warning("Сначала сохраните номенклатуру");
                          return;
                        }
                        setIsAddPriceModalOpen(true);
                      }}
                    >
                      Добавить цену
                    </Button>
                  </div>
                  {isAddPriceModalOpen && (
                    <PricesContext.Provider
                      value={{
                        token,
                        pathname: "/prices",
                        priceTypesData: priceTypes,
                      }}
                    >
                      <Suspense fallback={<div>Загрузка...</div>}>
                        <AddPrice
                          isOpen={isAddPriceModalOpen}
                          setOpen={(open) => {
                            setIsAddPriceModalOpen(open);
                            if (!open) {
                              loadPrices(); // Перезагружаем цены после закрытия
                            }
                          }}
                          initialNomenclature={record?.id}
                        />
                      </Suspense>
                    </PricesContext.Provider>
                  )}
                  <Table
                    dataSource={prices.filter(Boolean)}
                    loading={loadingPrices}
                    rowKey={(record, index) => record?.id ?? `row-${index}`}
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: "Тип цены",
                        dataIndex: "price_type",
                        key: "price_type",
                        render: (value) => {
                          const priceType = priceTypes.find(
                            (pt) => pt.id === value || pt.name === value
                          );
                          return priceType?.name || value || "-";
                        },
                      },
                      {
                        title: "Цена",
                        dataIndex: "price",
                        key: "price",
                        render: (value) => (value ? `${value} ₽` : "-"),
                      },
                      {
                        title: "Адрес",
                        dataIndex: "address",
                        key: "address",
                        ellipsis: true,
                        render: (value) => value || "-",
                      },
                      {
                        title: "Период действия",
                        key: "period",
                        render: (_, record) => {
                          const from = record.date_from
                            ? new Date(record.date_from * 1000).toLocaleDateString("ru-RU")
                            : "-";
                          const to = record.date_to
                            ? new Date(record.date_to * 1000).toLocaleDateString("ru-RU")
                            : "-";
                          return from !== "-" || to !== "-" ? `${from} - ${to}` : "Без ограничений";
                        },
                      },
                      {
                        title: "Действия",
                        key: "actions",
                        width: 100,
                        onCell: () => ({
                          style: {
                            paddingLeft: "8px",
                            paddingRight: "8px",
                          },
                        }),
                        render: (_, priceRecord) => (
                          <Space size="small">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setEditingPriceRecord(priceRecord);
                                setIsEditPriceModalOpen(true);
                              }}
                            />
                            <Popconfirm
                              title="Удалить цену?"
                              onConfirm={async () => {
                                try {
                                  await apiRequests.prices.delete({
                                    params: { token, ids: priceRecord.id.toString() },
                                  });
                                  messageApi.success("Цена удалена");
                                  loadPrices();
                                } catch (error) {
                                  messageApi.error("Ошибка при удалении цены");
                                  console.error(error);
                                }
                              }}
                            >
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                              />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                  {isEditPriceModalOpen && editingPriceRecord && (
                    <PricesContext.Provider
                      value={{
                        token,
                        pathname: "/prices",
                        priceTypesData: priceTypes,
                      }}
                    >
                      <Suspense fallback={<div>Загрузка...</div>}>
                        <EditPrice
                          record={editingPriceRecord}
                          handleSave={async (data) => {
                            try {
                              // Используем API.crud.edit для правильного формата запроса
                              const editPrice = API.crud.edit(token, "/prices", messageApi);
                              const response = await editPrice(data, data.id);
                              messageApi.success("Цена успешно обновлена");
                              await loadPrices();
                              return response;
                            } catch (error) {
                              messageApi.error("Ошибка при обновлении цены");
                              throw error;
                            }
                          }}
                          externalIsOpen={isEditPriceModalOpen}
                          onExternalClose={() => {
                            setIsEditPriceModalOpen(false);
                            setEditingPriceRecord(null);
                          }}
                          buttonProps={{
                            style: { display: "none" },
                          }}
                        />
                      </Suspense>
                    </PricesContext.Provider>
                  )}
                </>
              ),
            },
            {
              key: "videos",
              label: (
                <span>
                  <VideoCameraOutlined /> Видео
                </span>
              ),
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        if (!record?.id) {
                          messageApi.warning("Сначала сохраните номенклатуру");
                          return;
                        }
                        setIsAddVideoOpen(true);
                      }}
                    >
                      Добавить видео
                    </Button>
                  </div>

                  <Table
                    dataSource={videos}
                    loading={loadingVideos}
                    rowKey={(r) => r.id ?? r._key ?? Math.random()}
                    pagination={false}
                    size="small"
                    locale={{ emptyText: "Видео не добавлены" }}
                    columns={[
                      {
                        title: "Ссылка",
                        dataIndex: "url",
                        key: "url",
                        ellipsis: true,
                        render: (url) =>
                          url ? (
                            <a href={url} target="_blank" rel="noreferrer">
                              {url}
                            </a>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        title: "Описание",
                        dataIndex: "description",
                        key: "description",
                        ellipsis: true,
                        render: (v) => v || "-",
                      },
                      {
                        title: "Теги",
                        dataIndex: "tags",
                        key: "tags",
                        render: (tags) =>
                          Array.isArray(tags) && tags.length > 0 ? (
                            <Space size={4} wrap>
                              {tags.map((tag) => (
                                <Tag key={tag} color="blue">
                                  {tag}
                                </Tag>
                              ))}
                            </Space>
                          ) : (
                            "-"
                          ),
                      },
                      {
                        title: "Создано",
                        dataIndex: "created_at",
                        key: "created_at",
                        width: 130,
                        render: (v) =>
                          v
                            ? new Date(v).toLocaleDateString("ru-RU", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-",
                      },
                      {
                        title: "Действия",
                        key: "actions",
                        width: 90,
                        render: (_, videoRecord) => (
                          <Space size="small">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => {
                                setEditingVideo(videoRecord);
                                setIsEditVideoOpen(true);
                              }}
                            />
                            <Popconfirm
                              title="Удалить видео?"
                              onConfirm={async () => {
                                try {
                                  await apiRequests.nomenclatureVideos.deleteItem(
                                    videoRecord.id,
                                    { token }
                                  );
                                  messageApi.success("Видео удалено");
                                  loadVideos();
                                } catch (error) {
                                  messageApi.error("Ошибка при удалении видео");
                                  console.error(error);
                                }
                              }}
                            >
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                              />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />

                  {/* Add Video Modal */}
                  <VideoFormModal
                    open={isAddVideoOpen}
                    onClose={() => setIsAddVideoOpen(false)}
                    onSave={async (values) => {
                      try {
                        await apiRequests.nomenclatureVideos.createItem(
                          record.id,
                          values,
                          { token }
                        );
                        messageApi.success("Видео добавлено");
                        setIsAddVideoOpen(false);
                        loadVideos();
                      } catch (error) {
                        messageApi.error("Ошибка при добавлении видео");
                        console.error(error);
                      }
                    }}
                  />

                  {/* Edit Video Modal */}
                  <VideoFormModal
                    open={isEditVideoOpen}
                    initialValues={editingVideo}
                    onClose={() => {
                      setIsEditVideoOpen(false);
                      setEditingVideo(null);
                    }}
                    onSave={async (values) => {
                      try {
                        await apiRequests.nomenclatureVideos.patchItem(
                          editingVideo.id,
                          values,
                          { token }
                        );
                        messageApi.success("Видео обновлено");
                        setIsEditVideoOpen(false);
                        setEditingVideo(null);
                        loadVideos();
                      } catch (error) {
                        messageApi.error("Ошибка при обновлении видео");
                        console.error(error);
                      }
                    }}
                  />
                </>
              ),
            },
          ]}
        />
        </Form>
      </>
    );
  },
);

Nomenclature.displayName = "Nomenclature";

export default Nomenclature;